// Server-synced user settings bag.
//
// NOTE: this is distinct from `@/stores/userSettingsStore` (which is the
// localStorage-backed research-creation defaults — `maxTotalNodes`, etc.). This store mirrors the Postgres-backed user
// settings row exposed via the `/api/v1/users/me/settings` WS migration
// (Wave 1). Keys here are free-form (e.g. `diagram_enhancement_enabled`,
// `expertise_areas`, `topic_overrides`) — add a typed selector at the call
// site rather than baking keys into the store shape.
import { create as createProto } from '@bufbuild/protobuf';
import type { JsonObject, JsonValue } from '@bufbuild/protobuf';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createEnvelope } from '@/lib/protobuf/codec';
import {
  GetUserSettingsRequestSchema,
  UpdateUserSettingsRequestSchema,
} from '@/lib/protobuf/gen/settings/settings_pb';
import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import {
  DEFAULT_REQUEST_TIMEOUT_MS,
  createRequestTimers,
  timeoutError,
} from '@/lib/websocket/requestTimeout';
import { RequestError, sendRequest, wsManager } from '@/lib/websocket';
import { AppError } from '@/lib/errors';
import type { AsyncState } from '@/types/common';

// User-level settings ship as a google.protobuf.Struct on the wire. The
// protobuf-es TS generator maps Struct to JsonObject, so no structpb Value
// conversion is needed — values are plain JSON (boolean / string / number /
// array / nested object / null).
export type UserSettingsBag = JsonObject;

/** The outcome of one `update` call, correlated to that call alone. */
export type UpdateOutcome = { ok: true } | { ok: false; error: AppError };

interface UserSettingsStoreState {
  settings: UserSettingsBag;
  loadState: AsyncState<void>;  // initial GET lifecycle
  saveState: AsyncState<void>;  // in-flight UPDATE lifecycle

  actions: {
    load: () => void;
    /**
     * Merge `patch` into the bag optimistically and persist it. Resolves when
     * the server has answered (or the request timed out) — it never rejects,
     * and a rejected patch is un-merged.
     *
     * Resolves with THIS call's own outcome.
     *
     * The result is returned rather than only published to `saveState` because
     * that slot is global to the whole bag: another block's write landing
     * between a caller's `await` and its read of `saveState` would report the
     * wrong outcome in both directions (a foreign error shown under this
     * control, or — worse — "Saved" for a value the server refused and the
     * store already rolled back). That is CLAUDE.md's "single-slot flags for
     * request correlation are a race condition", so the correlated channel is
     * the return value. `saveState` remains for coarse global spinners.
     */
    update: (patch: UserSettingsBag) => Promise<UpdateOutcome>;
    handleSettingsResponse: (envelope: MessageEnvelope) => void;
  };
}

// Only the initial GET needs a hand-rolled timer: the save path runs through
// `sendRequest`, which owns its own timeout and — unlike a bare
// `onMessage('userSettingsResponse')` subscription — correlates by requestId,
// so somebody else's reply cannot resolve our save.
const timers = createRequestTimers<'load'>();

/**
 * Undo an optimistic merge, scoped to the keys the failed patch touched: a
 * wholesale snapshot restore would also discard a concurrent, successful write
 * to an unrelated key. A key that had no prior value is deleted rather than set
 * to `undefined` — `'k' in bag` is what the readers ask.
 */
function revertPatch(
  current: UserSettingsBag,
  prior: UserSettingsBag,
  patch: UserSettingsBag,
): UserSettingsBag {
  const next: UserSettingsBag = { ...current };
  for (const key of Object.keys(patch)) {
    if (Object.hasOwn(prior, key)) next[key] = prior[key] as JsonValue;
    else delete next[key];
  }
  return next;
}

/**
 * AsyncState carries an AppError, while `sendRequest` rejects with a
 * `RequestError` (whose `.message` is already the warm, catalog-resolved
 * string — for a CodeValidation rejection that is the server's own reason,
 * verbatim, because the catalog lists VALIDATION as DETAIL_PREFERRING) or a
 * NetworkError on timeout. Normalise without flattening the message.
 */
function asAppError(err: unknown): AppError {
  if (err instanceof AppError) return err;
  if (err instanceof RequestError) {
    return new AppError(
      err.wire.codeName ?? 'WIRE_ERROR',
      err.message,
      err.wire.retryable ?? false,
    );
  }
  return new AppError('UNKNOWN_ERROR', err instanceof Error ? err.message : String(err));
}

export const useUserSettingsStore = create<UserSettingsStoreState>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        settings: {} as UserSettingsBag,
        loadState: { status: 'idle' } as AsyncState<void>,
        saveState: { status: 'idle' } as AsyncState<void>,

        actions: {
          load: () => {
            set((state) => {
              state.loadState = { status: 'loading' };
            });
            timers.arm('load', DEFAULT_REQUEST_TIMEOUT_MS, () => {
              // Only flip to error if still loading — a late response clears
              // the state first and the timeout becomes a no-op.
              if (get().loadState.status === 'loading') {
                set((state) => {
                  state.loadState = { status: 'error', error: timeoutError('load user settings') };
                });
              }
            });
            const req = createProto(GetUserSettingsRequestSchema, {});
            wsManager.sendEnvelope(createEnvelope({ case: 'getUserSettingsRequest', value: req }));
          },

          // Routed through `sendRequest`, not raw `sendEnvelope` (TASKS-15 D6).
          // The backend rejects an invalid `timezone` with a 400 carrying a
          // curated reason (`internal/settings/ws_handler.go`), and an error
          // envelope matches no `onMessage('userSettingsResponse')`
          // subscription — so the old code left the refused value merged in the
          // bag, spun for 30 s, and then reported a TIMEOUT instead of the
          // reason. `sendRequest` correlates by requestId and sees both
          // carriers, which is what makes the 400 channel consumable at all.
          update: async (patch) => {
            // Optimistic merge; the server returns the merged state, which wins.
            const prior = get().settings;
            set((state) => {
              state.settings = { ...state.settings, ...patch };
              state.saveState = { status: 'loading' };
            });

            const req = createProto(UpdateUserSettingsRequestSchema, {
              settings: patch,
            });
            try {
              const res = await sendRequest(
                { case: 'updateUserSettingsRequest', value: req },
                'userSettingsResponse',
              );
              set((state) => {
                state.settings = res.settings ?? {};
                state.saveState = { status: 'success', data: undefined };
              });
              return { ok: true };
            } catch (err) {
              // A write the server refused (or never confirmed) must not stay
              // on screen looking saved — the status alone is not the fix.
              const appErr = asAppError(err);
              set((state) => {
                state.settings = revertPatch(state.settings, prior, patch);
                state.saveState = { status: 'error', error: appErr };
              });
              return { ok: false, error: appErr };
            }
          },

          // The LOAD lifecycle only. It used to resolve `saveState` too, on the
          // grounds that any response proves the socket is alive — but the two
          // directions share a payload case, so a load reply arriving mid-save
          // reported the save as succeeded (and overwrote the optimistic bag).
          // `update` now owns `saveState` end to end, correlated by requestId.
          handleSettingsResponse: (envelope) => {
            if (envelope.payload.case !== 'userSettingsResponse') return;
            const val = envelope.payload.value;
            timers.clear('load');
            set((state) => {
              state.settings = val.settings ?? {};
              state.loadState = { status: 'success', data: undefined };
            });
          },
        },
      })),
    ),
    { name: 'UserSettingsStore' },
  ),
);

export function registerUserSettingsHandlers(): () => void {
  const { actions } = useUserSettingsStore.getState();
  const unsubscribe = wsManager.onMessage('userSettingsResponse', actions.handleSettingsResponse);
  return () => unsubscribe();
}

// Re-exported so settings-page consumers can narrow values without re-importing
// the protobuf types.
export type { JsonValue };
